(() => {
  const script = document.currentScript;
  const storeCode = script?.dataset?.pricelabStore || "";
  const endpoint = script?.dataset?.pricelabEndpoint || "";
  const storefrontEpoch = script?.dataset?.pricelabStorefrontEpoch || "";
  if (!storeCode || !endpoint.startsWith("https://")) return;

  const anonymousKey = "crystal_pricelab_visitor_v1";
  const assignmentKey = "crystal_pricelab_assignments_v1";
  const propertyName = "properties[_pricelab_v1]";
  const productFormSelector =
    'main product-form form[action*="/cart/add"]';
  const pendingAssignments = new Map();

  const randomId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    const bytes = new Uint8Array(16);
    window.crypto?.getRandomValues?.(bytes);
    return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
  };

  const anonymousId = () => {
    let value = localStorage.getItem(anonymousKey);
    if (!value || value.length < 16) {
      value = randomId();
      localStorage.setItem(anonymousKey, value);
    }
    return value;
  };

  const readState = () => {
    try {
      const state = JSON.parse(localStorage.getItem(assignmentKey) || "{}");
      return {
        version: 1,
        assignments: state.assignments || {},
      };
    } catch (_) {
      return {version: 1, assignments: {}};
    }
  };

  const writeAssignment = (assignment) => {
    const state = readState();
    Object.entries(state.assignments).forEach(([key, value]) => {
      if (
        !value?.expiresAt ||
        Date.parse(value.expiresAt) <= Date.now() ||
        value.storefrontEpoch !== storefrontEpoch
      ) {
        delete state.assignments[key];
      }
    });
    if (assignment?.active && assignment.experimentId) {
      const variantId = String(assignment.variantId || "").split("/").pop();
      state.assignments[`${assignment.experimentId}:${variantId}`] = {
        ...assignment,
        storefrontEpoch,
      };
    }
    localStorage.setItem(assignmentKey, JSON.stringify(state));
  };

  const activeAssignment = (variantId) => {
    const normalized = String(variantId || "").split("/").pop();
    return (
      Object.values(readState().assignments).find(
        (assignment) =>
          assignment?.active &&
          assignment.storefrontEpoch === storefrontEpoch &&
          String(assignment.variantId) === normalized &&
          Date.parse(assignment.expiresAt || "") > Date.now(),
      ) || null
    );
  };

  const syncFormProperty = (form, assignment) => {
    let input = form.querySelector(`[name="${propertyName}"]`);
    if (!assignment) {
      input?.remove();
      return;
    }
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = propertyName;
      input.dataset.pricelabAssignment = "true";
      form.append(input);
    }
    input.value = assignment.token;
  };

  const publishExposure = (assignment) => {
    const exposureKey = [
      "crystal_pricelab_exposure_v1",
      assignment.assignmentId,
      assignment.variantId,
    ].join(":");
    if (sessionStorage.getItem(exposureKey)) return;
    if (!window.Shopify?.analytics?.publish) return;
    sessionStorage.setItem(exposureKey, "1");
    window.Shopify.analytics.publish("crystal_pricelab_exposure", {
      assignmentId: assignment.assignmentId,
      experimentKey: assignment.experimentKey,
      armKey: assignment.armKey,
      variantId: assignment.variantId,
      displayedPrice: assignment.assignedPrice,
      storefrontEpoch,
    });
  };

  const fetchAssignment = async (variantId) => {
    const normalized = String(variantId || "").split("/").pop();
    if (!normalized) return null;
    const existing = activeAssignment(normalized);
    if (existing) return existing;
    const pending = pendingAssignments.get(normalized);
    if (pending) return pending;
    const request = (async () => {
      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {"Content-Type": "application/json"},
          body: JSON.stringify({
            variantId: normalized,
            anonymousId: anonymousId(),
          }),
        });
        if (!response.ok) return null;
        const assignment = await response.json();
        if (!assignment.active) return null;
        writeAssignment(assignment);
        return assignment;
      } catch (_) {
        return null;
      }
    })();
    pendingAssignments.set(normalized, request);
    try {
      return await request;
    } finally {
      if (pendingAssignments.get(normalized) === request) {
        pendingAssignments.delete(normalized);
      }
    }
  };

  const syncForm = async (form) => {
    const variantInput = form.querySelector('[name="id"]');
    const variantId = variantInput?.value;
    if (!variantId) return;
    const assignment = await fetchAssignment(variantId);
    if (String(variantInput.value) !== String(variantId)) return;
    syncFormProperty(form, assignment);
    if (assignment) publishExposure(assignment);
  };

  const syncAllForms = (root = document) => {
    const forms = [];
    if (
      root instanceof HTMLFormElement &&
      root.matches(productFormSelector)
    ) {
      forms.push(root);
    }
    root
      .querySelectorAll?.(productFormSelector)
      .forEach((form) => forms.push(form));
    [...new Set(forms)].forEach((form) => syncForm(form));
  };

  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const variantId = form.querySelector('[name="id"]')?.value;
      syncFormProperty(form, activeAssignment(variantId));
    },
    true,
  );

  document.addEventListener("change", (event) => {
    if (event.target?.matches?.('[name="id"], [data-option-value-id]')) {
      const form = event.target.closest("form") || document;
      setTimeout(() => syncAllForms(form), 0);
    }
  });

  new MutationObserver((mutations) => {
    if (
      mutations.some(
        (mutation) =>
          mutation.type === "childList" ||
          mutation.target?.matches?.('[name="id"]'),
      )
    ) {
      syncAllForms();
    }
  }).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["value"],
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => syncAllForms(), {once: true});
  } else {
    syncAllForms();
  }
})();
